import { inject, Injectable } from "@angular/core";
import { BlApiService } from "./bl-api.service";
import { BlState, BlStoreService } from "./bl-store.service";
import { PaginatedEntityFacadeService } from "../../../../shared/store/pagination/paginated-entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { BonLivraison } from "../../models/bon-livraison.model";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { BlPayloadEnricherService } from "../bl-payload-enricher.service";
import { SnackService } from "../../../../shared/services/snack-service.service";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { BlMailPayload } from "../../models/bl-mail-payload.model";

@Injectable({ providedIn: "root" })
export class BlFacadeService extends PaginatedEntityFacadeService<
  BonLivraison,
  BlApiService,
  EntityMetadata,
  BlStoreService
> {
  dialogService = inject(DialogsService);
  private payloadEnricher = inject(BlPayloadEnricherService);
  private snackService = inject(SnackService);

  constructor() {
    const api = inject(BlApiService);
    const store = inject(BlStoreService);
    super(api, store);
    store.initStatus(BlFacadeService);
  }

  @Untrack
  @UpdateStatus
  async loadAllNotBilled() {
    this.store.removeAll();
    const expes = await firstValueFrom(this.api.loadAllNotBilled());
    expes.forEach((e) => {
      this.store.upsertOne(e);
    });
    return expes;
  }

  @Untrack
  @UpdateStatus
  async sendMail(payload: BlMailPayload) {
    await firstValueFrom(this.api.sendMail(payload));
    this.snackService.success(TranslateUtils.translate("bl.mail_sent"));
  }

  @Untrack
  @UpdateStatus
  async getBlPdf(blId: string) {
    await this.dialogService.openPdfLoader(async () => {
      const payload = await firstValueFrom(this.api.loadBlPayload(blId));
      if (payload) {
        const enriched = this.payloadEnricher.enrichPayload(payload);
        const firstBl = (payload as any)?.bls?.[0];
        const numero = firstBl?.numeroBL ? firstBl.numeroBL.replace(/-/g, '') : 'BL';
        const tiers = firstBl?.commandeClient?.tiers?.denomination || '';
        const title = tiers ? `${numero} - ${tiers}` : numero;
        await this.api.printPdfBL(enriched, title);
      }
    });
  }

  /**
   * Imprime un **BL provisoire (brouillon)** pour une commande client,
   * construit depuis les unités d'expédition en préparation (aucun BL créé,
   * numéro « PROVISOIRE »).
   */
  @Untrack
  @UpdateStatus
  async getProvisionalBlPdf(commandeClientId: string) {
    await this.dialogService.openPdfLoader(async () => {
      const payload = await firstValueFrom(
        this.api.loadProvisionalPayload(commandeClientId),
      );
      if (payload) {
        const enriched = this.payloadEnricher.enrichPayload(payload);
        const firstBl = (payload as any)?.bls?.[0];
        const tiers = firstBl?.commandeClient?.tiers?.denomination || '';
        const title = tiers ? `BL provisoire - ${tiers}` : 'BL provisoire';
        await this.api.printPdfBL(enriched, title);
      }
    });
  }

  /**
   * Récupère le blob URL du PDF d'un BL (sans déclencher l'impression).
   * Utile depuis un dialog où `printJS` ne peut pas s'afficher correctement
   * (focus capté par l'overlay) — le caller ouvre l'URL dans un nouvel onglet.
   */
  @Untrack
  @UpdateStatus
  async getBlPdfBlobUrl(blId: string): Promise<string | null> {
    const payload = await firstValueFrom(this.api.loadBlPayload(blId));
    if (!payload) return null;
    const enriched = this.payloadEnricher.enrichPayload(payload);
    return this.api.fetchPdfBLBlobUrl(enriched);
  }
}
